import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
}

export function usePushNotifications() {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [loading, setLoading] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");

  useEffect(() => {
    const hasSW = "serviceWorker" in navigator;
    const hasPush = "PushManager" in window;
    const hasNotif = "Notification" in window;
    const supported = hasSW && hasPush && hasNotif;
    const info = `SW:${hasSW} Push:${hasPush} Notif:${hasNotif} Standalone:${(window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches}`;
    console.log("Push support check:", info);
    setDebugInfo(info);
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
      checkExistingSubscription();
    }
    loadVapidKey();
  }, []);

  const loadVapidKey = async () => {
    try {
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "vapid_public_key")
        .maybeSingle();
      if (data?.value) {
        setVapidPublicKey(data.value);
      }
    } catch (e) {
      console.error("Load VAPID key error:", e);
    }
  };

  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration("/sw.js");
      if (registration) {
        const subscription = await (registration as any).pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      }
    } catch (e) {
      console.error("Check subscription error:", e);
    }
  };

  const subscribe = useCallback(async (): Promise<{ success: boolean; reason?: string }> => {
    if (!user) { console.error("Push: No user"); return { success: false, reason: "Kullanıcı oturumu bulunamadı. Lütfen tekrar giriş yapın." }; }
    if (!isSupported) { console.error("Push: Not supported"); return { success: false, reason: "Bu cihaz/tarayıcı push bildirimleri desteklemiyor." }; }
    if (!vapidPublicKey) { console.error("Push: No VAPID key"); return { success: false, reason: "VAPID anahtarı yüklenemedi. İnternet bağlantınızı kontrol edin." }; }
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      console.log("Push: SW registered", registration.scope);

      const ready = await navigator.serviceWorker.ready;
      console.log("Push: SW ready");

      const existingSub = await (ready as any).pushManager.getSubscription();
      if (existingSub) {
        console.log("Push: Unsubscribing existing");
        await existingSub.unsubscribe();
      }

      const perm = await Notification.requestPermission();
      setPermission(perm);
      console.log("Push: Permission result:", perm);
      if (perm !== "granted") {
        setLoading(false);
        return { success: false, reason: `Bildirim izni reddedildi (${perm}). Cihaz ayarlarından izin verin.` };
      }

      console.log("Push: Subscribing with VAPID key:", vapidPublicKey.substring(0, 20) + "...");
      const subscription = await (ready as any).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
      console.log("Push: Subscribed, endpoint:", subscription.endpoint.substring(0, 60) + "...");

      const subJson = subscription.toJSON();

      await supabase.from("push_subscriptions").delete().eq("user_id", user.id);
      const { error } = await supabase.from("push_subscriptions").insert({
        user_id: user.id,
        endpoint: subJson.endpoint!,
        p256dh: subJson.keys!.p256dh!,
        auth: subJson.keys!.auth!,
      });

      if (error) {
        console.error("Push: DB insert error:", error);
        setLoading(false);
        return { success: false, reason: `Veritabanı hatası: ${error.message}` };
      }
      console.log("Push: Subscription saved to DB");
      setIsSubscribed(true);
      setLoading(false);
      return { success: true };
    } catch (e: any) {
      const msg = e?.message || String(e);
      console.error("Push subscribe error:", msg);
      setLoading(false);
      return { success: false, reason: `Hata: ${msg}` };
    }
  }, [user, isSupported, vapidPublicKey]);

  const unsubscribe = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration("/sw.js");
      if (registration) {
        const subscription = await (registration as any).pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          await supabase
            .from("push_subscriptions")
            .delete()
            .eq("user_id", user.id)
            .eq("endpoint", subscription.endpoint);
        }
      }
      setIsSubscribed(false);
    } catch (e) {
      console.error("Push unsubscribe error:", e);
    }
    setLoading(false);
  }, [user]);

  return { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe, vapidPublicKey, debugInfo };
}
